'use client';
import { useEffect, useId, useState } from 'react';

import { Input } from './FormField';

export interface NameSuggestion {
  name: string;
  playerId: string | null;
}
export function PlayTogetherName({
  leagueKey,
  placeholder,
  helper = 'Start typing your friend’s name. If they haven’t registered yet, you can still leave a request.',
  value,
  selectedPlayerId,
  onChange,
  lookup,
  name = 'sitWithName',
  identityName = 'sitWithPlayerId',
}: {
  leagueKey: string;
  placeholder?: string;
  helper?: string;
  value: string;
  selectedPlayerId: string | null;
  onChange: (name: string, id: string | null) => void;
  lookup: (query: string) => Promise<NameSuggestion[]>;
  name?: string;
  identityName?: string;
}) {
  const id = useId(),
    [result, setResult] = useState<{ key: string; items: NameSuggestion[]; error?: boolean }>(),
    [busy, setBusy] = useState(false),
    [active, setActive] = useState(-1),
    [open, setOpen] = useState(true);
  const query = value.trim(),
    key = `${leagueKey}:${query}`,
    eligible = query.replace(/\s/g, '').length >= 2 && !selectedPlayerId;
  useEffect(() => {
    let current = true;
    setActive(-1);
    if (!eligible) {
      setBusy(false);
      return;
    }
    setBusy(true);
    const timer = setTimeout(() => {
      lookup(query)
        .then((items) => {
          if (current) setResult({ key, items });
        })
        .catch(() => {
          if (current) setResult({ key, items: [], error: true });
        })
        .finally(() => {
          if (current) setBusy(false);
        });
    }, 200);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [key, eligible, lookup, query]);
  const items = eligible && result?.key === key ? result.items : [],
    shown = open && eligible;
  const select = (item: NameSuggestion) => {
    if (!item.playerId) return;
    onChange(item.name, item.playerId);
    setOpen(false);
  };
  return (
    <div className="flex min-w-0 w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold">
        Who would you like to sit with?
      </label>
      <Input
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown && items.length > 0}
        aria-controls={`${id}-list`}
        aria-describedby={`${id}-help ${id}-status`}
        aria-activedescendant={shown && active >= 0 ? `${id}-${active}` : undefined}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value, null);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            return;
          }
          if (shown && items.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setActive((n) => (n + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length);
          }
          if (e.key === 'Enter' && shown && active >= 0) {
            e.preventDefault();
            select(items[active]!);
          }
        }}
      />
      <input type="hidden" name={identityName} value={selectedPlayerId ?? ''} />
      <ul id={`${id}-list`} role="listbox" className="empty:hidden">
        {shown
          ? items.map((item, i) => (
              <li
                key={`${item.name}:${i}`}
                id={`${id}-${i}`}
                role="option"
                aria-selected={active === i}
                aria-disabled={!item.playerId}
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => select(item)}
                className={`min-h-[44px] cursor-pointer rounded-md p-3 text-[14px] ${active === i ? 'bg-sand' : 'bg-paper'}`}
              >
                {item.name}
                {!item.playerId
                  ? ' — more than one match; leave this name for Director review.'
                  : ''}
              </li>
            ))
          : null}
      </ul>
      <p id={`${id}-help`} className="text-[12px] text-ink-2">
        {helper}
      </p>
      <p id={`${id}-status`} role="status" className="text-[13px] text-ink-2">
        {selectedPlayerId
          ? 'Registered player selected.'
          : shown
            ? busy
              ? 'Looking for matching names…'
              : result?.key === key && result.error
                ? 'Names could not be loaded. You can still submit your typed request.'
                : result?.key === key && !items.length
                  ? 'No matching names. You can still leave this request.'
                  : ''
            : ''}
      </p>
    </div>
  );
}
